import { useState, type FormEvent } from 'react';
import { entryInput } from '@redirector/shared';
import { Input } from '../components/ui/input.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Dialog } from '../ui/Dialog.js';
import { Field } from '../ui/Field.js';
import { issuesToFieldErrors, type FieldErrors } from '../ui/formErrors.js';
import { useCloneEntry } from '../domains/queries.js';
import { displayUrl, parseFullUrl, sameHost } from './parseUrl.js';
import { useInvalidateUrls, useUrls, type UrlRow } from './queries.js';

/** The URL being cloned, as the detail page already has it. */
export type CloneSource = {
  entryId: number;
  domainId: number;
  hostname: string;
  path: string;
  pathNorm: string;
  label: string;
};

export type CloneUrlDialogProps = {
  open: boolean;
  source: CloneSource;
  onClose: () => void;
  /** Receives the clone's entry id, so the caller can open it. */
  onCloned?: (entryId: number) => void;
};

const MONO = 'font-mono text-[length:var(--text-small)]';

function submitErrors(error: unknown): FieldErrors {
  const fields =
    error !== null && typeof error === 'object' && 'fields' in error
      ? (error as { fields: FieldErrors }).fields
      : {};
  if (fields.path !== undefined) return { url: fields.path };
  if (fields.label !== undefined) return fields;
  return { root: error instanceof Error ? error.message : 'Could not clone the URL' };
}

/**
 * Clone a URL to a second path on the same hostname.
 *
 * A clone carries the source's destinations and weights over, so the only
 * thing that varies is the path — the hostname, and therefore the group, are
 * fixed by the URL being cloned. The field is still a whole URL rather than a
 * bare path so that it matches the add-URL flow and can be pasted into; a
 * different hostname is rejected rather than silently retargeted.
 */
export function CloneUrlDialog({ open, source, onClose, onCloned }: CloneUrlDialogProps) {
  return (
    <Dialog open={open} title="Clone URL" onClose={onClose}>
      {open && (
        <CloneUrlForm
          source={source}
          onCancel={onClose}
          onDone={onClose}
          {...(onCloned === undefined ? {} : { onCloned })}
        />
      )}
    </Dialog>
  );
}

type CloneUrlFormProps = {
  source: CloneSource;
  onCloned?: (entryId: number) => void;
  onDone: () => void;
  onCancel: () => void;
};

function CloneUrlForm({ source, onCloned, onDone, onCancel }: CloneUrlFormProps) {
  const clone = useCloneEntry(source.domainId);
  const invalidateUrls = useInvalidateUrls();
  /* Unfiltered: the clone's target path must be checked against every URL on
     this domain, not just the ones a filtered screen happens to be showing. */
  const urls = useUrls();

  /* Prefilled with the source URL, because only the tail of the path usually
     changes. It also makes "you have not changed it" a real state to catch
     rather than an empty field the operator has to guess the shape of. */
  const [url, setUrl] = useState(displayUrl(source.hostname, source.path));
  const [label, setLabel] = useState(source.label === '' ? '' : `${source.label} (copy)`);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const parsed = url.trim() === '' ? null : parseFullUrl(url);
  const parts = parsed?.ok === true ? parsed.parts : null;

  const collision: UrlRow | undefined =
    parts === null
      ? undefined
      : urls.data?.urls.find(
          (row) => row.domainId === source.domainId && row.pathNorm === parts.pathNorm,
        );

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (parsed === null || !parsed.ok) {
      setErrors({ url: parsed === null ? 'A URL is required' : parsed.message });
      return;
    }

    if (!sameHost(parsed.parts.hostname, source.hostname)) {
      setErrors({
        url: `A clone stays on ${source.hostname}. To point a different hostname at these destinations, add a URL there instead.`,
      });
      return;
    }

    if (parsed.parts.pathNorm === source.pathNorm) {
      setErrors({ url: 'That is the URL you are cloning. Give the clone a different path.' });
      return;
    }

    if (collision !== undefined) {
      setErrors({
        url: `${collision.fullUrl} is already configured. Open that URL instead, or choose a different path.`,
      });
      return;
    }

    const entry = entryInput.safeParse({ label, path: parsed.parts.path, enabled: true });
    if (!entry.success) {
      const issues = issuesToFieldErrors(entry.error.issues);
      setErrors(issues.path === undefined ? issues : { ...issues, url: issues.path });
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      const created = await clone.mutateAsync({
        id: source.entryId,
        path: entry.data.path,
        label: entry.data.label,
      });
      invalidateUrls();
      onCloned?.(created.entry.id);
      onDone();
    } catch (error) {
      setErrors(submitErrors(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <p className="text-[length:var(--text-small)] text-ink-subtle">
        The clone starts with the same destinations and weights as{' '}
        <span className="font-mono text-ink-muted">
          {source.hostname}
          {source.path}
        </span>
        .
      </p>

      <Field
        label="New full URL"
        error={errors.url}
        hint="Same hostname, a different path. The scheme is ignored, and no query string — incoming parameters are forwarded automatically."
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            autoFocus
            className={MONO}
            spellCheck={false}
            autoCapitalize="none"
            inputMode="url"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        )}
      </Field>

      {parts !== null && (
        <p className="text-[length:var(--text-small)] text-ink-subtle">
          Matches{' '}
          <span className="font-mono text-ink-muted">
            {parts.hostname}
            {parts.pathNorm}
          </span>{' '}
          — matching ignores the scheme, capitalisation and a trailing slash.
        </p>
      )}

      <Field label="Label" error={errors.label} hint="Optional. What this campaign is, in human terms.">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        )}
      </Field>

      <div className="flex justify-end gap-(--space-2)">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Cloning…' : 'Create clone'}
        </Button>
      </div>
    </form>
  );
}
